import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Users, Package, Award, LifeBuoy, Briefcase, Layers, Building2, Globe, Check, ArrowRight, Star, Shield, BarChart3, Plug } from "lucide-react";
import { SectionHeader } from "@/components/ui/section-header";
import { Reveal } from "@/components/motion/Reveal";
import { StaggerGrid } from "@/components/motion/StaggerGrid";
import { HoverLift } from "@/components/motion/HoverLift";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useBootcampCount } from "@/hooks/useBootcampCount";

const enterpriseSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  company: z.string().trim().min(1).max(150),
  teamSize: z.string().trim().max(50),
  message: z.string().trim().min(10).max(2000),
});

export default function Enterprise() {
  const { t } = useTranslation();
  const bootcampCount = useBootcampCount();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", teamSize: "", message: "" });

  const features = [
    { icon: Users, title: t("marketing.enterprise.feat1Title"), desc: t("marketing.enterprise.feat1Desc") },
    { icon: Package, title: t("marketing.enterprise.feat2Title"), desc: t("marketing.enterprise.feat2Desc") },
    { icon: Award, title: t("marketing.enterprise.feat3Title"), desc: t("marketing.enterprise.feat3Desc") },
    { icon: LifeBuoy, title: t("marketing.enterprise.feat4Title"), desc: t("marketing.enterprise.feat4Desc") },
  ];

  const cases = [
    { quote: t("marketing.enterprise.case1"), org: t("marketing.enterprise.case1Org") },
    { quote: t("marketing.enterprise.case2"), org: t("marketing.enterprise.case2Org") },
    { quote: t("marketing.enterprise.case3"), org: t("marketing.enterprise.case3Org") },
  ];

  const useCases = [
    { Icon: Briefcase, title: t("marketing.enterprise.uc1Title"), desc: t("marketing.enterprise.uc1Desc") },
    { Icon: Layers, title: t("marketing.enterprise.uc2Title"), desc: t("marketing.enterprise.uc2Desc") },
    { Icon: Building2, title: t("marketing.enterprise.uc3Title"), desc: t("marketing.enterprise.uc3Desc") },
    { Icon: Globe, title: t("marketing.enterprise.uc4Title"), desc: t("marketing.enterprise.uc4Desc") },
  ];

  const enterpriseFeatures = [
    { Icon: Users, title: t("marketing.enterprise.f1Title"), desc: t("marketing.enterprise.f1Desc") },
    { Icon: Package, title: t("marketing.enterprise.f2Title"), desc: t("marketing.enterprise.f2Desc") },
    { Icon: LifeBuoy, title: t("marketing.enterprise.f3Title"), desc: t("marketing.enterprise.f3Desc") },
    { Icon: BarChart3, title: t("marketing.enterprise.f4Title"), desc: t("marketing.enterprise.f4Desc") },
    { Icon: Award, title: t("marketing.enterprise.f5Title"), desc: t("marketing.enterprise.f5Desc") },
    { Icon: Plug, title: t("marketing.enterprise.f6Title"), desc: t("marketing.enterprise.f6Desc") },
    { Icon: Star, title: t("marketing.enterprise.f7Title"), desc: t("marketing.enterprise.f7Desc") },
    { Icon: Shield, title: t("marketing.enterprise.f8Title"), desc: t("marketing.enterprise.f8Desc") },
  ];

  const tiers = [
    {
      name: t("marketing.enterprise.tier1Name"),
      sub: t("marketing.enterprise.tier1Sub"),
      items: [
        t("marketing.enterprise.tier1I1", { bootcamps: bootcampCount }),
        t("marketing.enterprise.tier1I2"),
        t("marketing.enterprise.tier1I3"),
        t("marketing.enterprise.tier1I4"),
      ],
      featured: false,
    },
    {
      name: t("marketing.enterprise.tier2Name"),
      sub: t("marketing.enterprise.tier2Sub"),
      items: [
        t("marketing.enterprise.tier2I1"),
        t("marketing.enterprise.tier2I2"),
        t("marketing.enterprise.tier2I3"),
        t("marketing.enterprise.tier2I4"),
      ],
      featured: true,
    },
    {
      name: t("marketing.enterprise.tier3Name"),
      sub: t("marketing.enterprise.tier3Sub"),
      items: [
        t("marketing.enterprise.tier3I1"),
        t("marketing.enterprise.tier3I2"),
        t("marketing.enterprise.tier3I3"),
        t("marketing.enterprise.tier3I4"),
      ],
      featured: false,
    },
  ];

  const trustedLogos = ["INNOVATION HUB", "AFRICA INC.", "GROWTH LAB", "STARTUP BRIDGE", "FUTURE WORKS"];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = enterpriseSchema.safeParse(form);
    if (!parsed.success) {
      toast({ title: t("marketing.contact.errorTitle"), description: t("marketing.contact.errorBody"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const subject = `Enterprise inquiry from ${parsed.data.company} (${parsed.data.teamSize || "size n/a"})`;
    const body = `Team size: ${parsed.data.teamSize || "n/a"}\n\n${parsed.data.message}`;
    const { data, error } = await supabase.rpc("submit_support_ticket", {
      _category: "Provider_Application_FollowUp",
      _priority: "P3",
      _subject: subject,
      _body: body,
      _email: parsed.data.email,
    });
    setSubmitting(false);
    if (error || !data) {
      toast({ title: t("marketing.contact.errorTitle"), description: error?.message ?? t("marketing.contact.errorBody"), variant: "destructive" });
      return;
    }
    toast({ title: t("marketing.enterprise.successTitle"), description: t("marketing.enterprise.successBody", { id: data }) });
    setForm({ name: "", email: "", company: "", teamSize: "", message: "" });
  }

  return (
    <PublicShell>
      <Seo title={t("marketing.enterprise.seoTitle")} description={t("marketing.enterprise.seoDescription")} />

      {/* Hero with dual CTA */}
      <Reveal as="section" className="container py-20 md:py-24">
        <SectionHeader
          align="center"
          eyebrow={t("marketing.enterprise.heroLabel")}
          title={t("marketing.enterprise.title")}
          lede={t("marketing.enterprise.subtitle")}
        />
        <div className="mx-auto mt-8 flex max-w-3xl flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" variant="brand">
            <a href="#contact">{t("marketing.enterprise.ctaPrimary")} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} /></a>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a href="#contact">{t("marketing.enterprise.ctaSecondary")}</a>
          </Button>
        </div>
      </Reveal>

      {/* Trusted-by logo strip */}
      <section className="border-y border-border bg-muted/30">
        <div className="container py-10">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {t("marketing.enterprise.trusted")}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-60">
            {trustedLogos.map((l) => (
              <span key={l} className="font-mono text-xs font-semibold tracking-wider text-foreground/70">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <Reveal as="section" className="container py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {t("marketing.enterprise.useCasesLabel")}
          </div>
          <h2 className="mt-3 t-display-md">{t("marketing.enterprise.useCasesH")}</h2>
        </div>
        <StaggerGrid className="mt-10 grid gap-6 sm:grid-cols-2">
          {useCases.map(({ Icon, title, desc }) => (
            <HoverLift key={title} className="rounded-xl h-full">
            <Card className="h-full">
              <CardContent className="p-6">
                <div className="mb-4 grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="text-lg font-semibold">{title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
              </CardContent>
            </Card>
            </HoverLift>
          ))}
        </StaggerGrid>
      </Reveal>

      {/* Original 4-feature band kept for back-compat */}
      <section className="border-y border-border bg-muted/30">
        <Reveal className="container py-16">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {t("marketing.enterprise.featuresLabel")}
            </div>
            <h2 className="mt-3 t-display-md">{t("marketing.enterprise.featuresH")}</h2>
          </div>
          <StaggerGrid className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {enterpriseFeatures.map(({ Icon, title, desc }) => (
              <HoverLift key={title} className="rounded-xl h-full">
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="mb-3 grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div className="text-base font-semibold">{title}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                </CardContent>
              </Card>
              </HoverLift>
            ))}
          </StaggerGrid>
        </Reveal>
      </section>

      {/* Pricing tiers */}
      <section className="container py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {t("marketing.enterprise.pricingLabel")}
          </div>
          <h2 className="mt-3 t-display-md">{t("marketing.enterprise.pricingH")}</h2>
          <p className="mt-3 text-muted-foreground">{t("marketing.enterprise.pricingSub")}</p>
        </div>
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 md:grid-cols-3">
          {tiers.map((tier) => (
            <Card key={tier.name} className={tier.featured ? "border-primary shadow-lg ring-1 ring-primary/20" : ""}>
              <CardContent className="flex h-full flex-col p-6">
                {tier.featured && (
                  <Badge className="mb-3 w-fit bg-primary/15 text-primary hover:bg-primary/20" variant="secondary">
                    {t("marketing.enterprise.tierMostPopular")}
                  </Badge>
                )}
                <div className="t-display-sm">{tier.name}</div>
                <div className="mt-1 text-sm text-muted-foreground">{tier.sub}</div>
                <ul className="mt-6 space-y-3">
                  {tier.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className="mt-6 w-full" variant={tier.featured ? "brand" : "outline"}>
                  <a href="#contact">{t("marketing.enterprise.ctaPrimary")}</a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Featured testimonial */}
      <section className="border-y border-border bg-muted/30">
        <div className="container py-16">
          <Card className="mx-auto max-w-3xl border-primary/30">
            <CardContent className="p-8 md:p-10">
              <p className="t-display-sm font-normal italic">
                "{t("marketing.enterprise.testimonialQuote")}"
              </p>
              <div className="mt-6 text-sm">
                <div className="font-semibold">{t("marketing.enterprise.testimonialName")}</div>
                <div className="text-muted-foreground">{t("marketing.enterprise.testimonialCo")}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Cases band kept */}
      <Reveal as="section" className="container py-16 md:py-20">
        <h2 className="text-center t-display-sm">{t("marketing.enterprise.casesTitle")}</h2>
        <StaggerGrid className="mt-8 grid gap-6 md:grid-cols-3">
          {cases.map((c, i) => (
            <HoverLift key={i} className="rounded-xl h-full">
            <Card className="h-full">
              <CardContent className="p-6">
                <p className="text-sm">"{c.quote}"</p>
                <div className="mt-3 text-xs font-medium text-muted-foreground">— {c.org}</div>
              </CardContent>
            </Card>
            </HoverLift>
          ))}
        </StaggerGrid>
      </Reveal>

      <section id="contact" className="border-t border-border">
        <Reveal className="container py-16">
          <div className="mx-auto max-w-2xl">
            <div className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {t("marketing.enterprise.ctaFinalH")}
            </div>
            <h2 className="t-display-md">{t("marketing.enterprise.formTitle")}</h2>
            <p className="mt-2 text-muted-foreground">{t("marketing.enterprise.ctaFinalSub")}</p>

            <form onSubmit={onSubmit} className="mt-8 grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="ent-name">{t("marketing.enterprise.fieldName")}</Label>
                  <Input id="ent-name" required maxLength={100} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ent-email">{t("marketing.enterprise.fieldEmail")}</Label>
                  <Input id="ent-email" type="email" required maxLength={255} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="ent-company">{t("marketing.enterprise.fieldCompany")}</Label>
                  <Input id="ent-company" required maxLength={150} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ent-size">{t("marketing.enterprise.fieldTeamSize")}</Label>
                  <Input id="ent-size" maxLength={50} value={form.teamSize} onChange={(e) => setForm({ ...form, teamSize: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ent-msg">{t("marketing.enterprise.fieldMessage")}</Label>
                <Textarea id="ent-msg" required minLength={10} maxLength={2000} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <Button type="submit" loading={submitting} size="lg" variant="brand">
                {submitting ? t("marketing.enterprise.submitting") : t("marketing.enterprise.submit")}
              </Button>
            </form>
          </div>
        </Reveal>
      </section>
    </PublicShell>
  );
}
